// FAQSection.js
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus } from 'lucide-react';

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: 'Why do prices vary?',
      answer: 'Pricing depends on destination, number of applications, and the level of support required. Each case is assessed individually to ensure you receive tailored guidance that matches your specific needs and goals.'
    },
    {
      question: 'Is the consultation really free?',
      answer: 'Yes. The initial consultation is free and designed to help you make an informed decision. There is no obligation to proceed after the consultation, and we will provide honest guidance on whether our services are suitable for your situation.'
    },
    {
      question: 'Do you offer refunds?',
      answer: 'Refund policies depend on the stage of service delivery and are explained clearly before payment. We believe in transparency and will outline all terms and conditions during the consultation process.'
    },
    {
      question: 'Can I pay in instalments?',
      answer: 'Instalment options may be available depending on the service and circumstances. This is discussed during consultation, and we will work with you to find a payment structure that suits your needs where possible.'
    },
    {
      question: 'Do you take a percentage of my tuition or salary?',
      answer: 'No. Notapaths does not take commissions on tuition, salaries, or scholarships. Our fees are for professional guidance and support services only, and are clearly communicated upfront.'
    },
    {
      question: 'When do I pay?',
      answer: 'Payments are made only after consultation, agreement on scope, and confirmation of services to be provided. You will always know exactly what you are paying for before any payment is made.'
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-16 bg-white">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-[#202356] mb-4">
            Frequently Asked Questions
          </h2>
        </motion.div>

        {/* FAQ Items */}
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-[#F5F8FA] rounded-lg shadow-sm overflow-hidden"
            >
              {/* Question */}
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-100 transition-colors"
              >
                <span className="text-lg font-semibold text-[#202356] pr-4">
                  {faq.question}
                </span>
                <div className="flex-shrink-0">
                  {openIndex === index ? (
                    <Minus size={24} className="text-[#2542D4]" />
                  ) : (
                    <Plus size={24} className="text-[#2542D4]" />
                  )}
                </div>
              </button>

              {/* Answer */}
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-6 text-gray-600 text-base font-light leading-relaxed">
                      {faq.answer}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
